import { useEffect, useMemo, useRef, useState } from 'react';
import { usePost } from '../../API';
import { useAsyncEffect } from '../../Hooks/AsyncEffect';
import { ProgressCircle, Badge, Button, Card, CardBody, CardHeader, Icon, Switch } from '@aidkitorg/component-library';
import { DocumentLibraryItem } from '@aidkitorg/types/lib/convoai/documentLibrary.types';
import { useAsyncInterval } from '../../Util';
import { useAsyncJobStatus } from '../../Hooks/AsyncJobStatusCheck';

export function DocumentGenerator() {

  const getAllDocuments = usePost('/ai/document_library/get_all')

  const [templates, setTemplates] = useState<DocumentLibraryItem[]>([]);
  const [selectionMap, setSelectionMap] = useState<Record<string, boolean>>({});

  useAsyncEffect(async () => {
    const response = await getAllDocuments({ templates: 'only' });

    if (!response.success || !response.data) {
      return;
    }

    setTemplates(response.data);

  }, []);

  const categoryMappedTemplates = useMemo(() => {
    return templates.reduce((categories, template) => ({
      ...categories,
      [template.category]: (categories[template.category] || []).concat(template)
    }), {} as Record<string, DocumentLibraryItem[]>);

  }, [templates]);

  const categoryTree = useMemo(() => Object.entries(categoryMappedTemplates)
    .map(([category, templates]) =>
      <CategoryCard
        key={category}
        category={category}
        templates={templates}
        selectionMap={selectionMap}
        onTemplateSelectionChanged={
          (updates) => setSelectionMap({
            ...selectionMap,
            ...updates
          })}
      />
    ), [selectionMap, categoryMappedTemplates]);

  return <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
    <div className="space-y-6">
      <div className="justify-between items-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Document Generator</h2>
        <div className="space-y-2 max-h-[70vh] overflow-y-scroll">
          {categoryTree}
        </div>
        <div>
          <GeneratedDocumentTracker
            templatesSelected={templates.filter(template => selectionMap[template.id])}
          />
        </div>
      </div>
    </div>
  </div>
}

type CategoryCardParams = {
  category: string,
  templates: DocumentLibraryItem[],
  selectionMap: Record<string, boolean>,
  onTemplateSelectionChanged(updates: Record<string, boolean>): void
};

function CategoryCard({ category, templates, selectionMap, onTemplateSelectionChanged }: CategoryCardParams) {

  const selectedTemplates = useMemo(() => {
    let selected = 0;
    for (const template of templates) {
      selected += selectionMap[template.id] ? 1 : 0;
    }
    return selected;
  }, [selectionMap, templates]);

  return (
    <Card>
      <CardHeader
        title={<div className="flex space-x-4">
          <Switch
            hideLabel={true}
            label={category}
            size="lg"
            checked={templates.length === selectedTemplates}
            onCheckedChange={checked => {
              onTemplateSelectionChanged(
                templates.reduce((map, template) => ({
                  ...map,
                  [template.id]: checked
                }), {} as Record<string, boolean>)
              );
            }}
          />
          <h4>{category}</h4>
        </div>
        }
        badgeText={selectedTemplates.toString()}
        badgeVariant={selectedTemplates ? 'info' : 'neutral'}
      />
      <CardBody className="space-y-2">
        <div className="pt-2 max-w-64 space-y-2">
          {templates.map(template =>
            <Switch
              key={template.id}
              label={template.original_filename}
              checked={selectionMap[template.id]}
              onCheckedChange={checked => onTemplateSelectionChanged({ [template.id]: checked })}
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function GenerationStatus({ taskId, template, onComplete }: { taskId: string, template: DocumentLibraryItem, onComplete(): void }) {
  const { phase, setTaskId } = useAsyncJobStatus();

  useEffect(() => setTaskId(taskId), [taskId]);

  useEffect(() => {
    switch (phase) {
      case 'FAILED':
      case 'FINISHED':
        onComplete();
        break;
      default:
        break;
    }
  }, [phase]);

  const icon = useMemo(() => {
    switch (phase) {
      case 'NOT_SUBMITTED':
        return <Icon name="EllipsisVerticalIcon" className="animate-pulse" />
      case 'QUEUED':
      case 'STARTED':
        return <Icon name="ArrowPathIcon" className="animate-spin" />
      case 'FINISHED':
        return <Icon name="CheckCircleIcon" color="green" />
      case 'FAILED':
        return <Icon name="XCircleIcon" color="red" />
    }
  }, [phase]);

  return <div className="flex items-center space-x-2">
    {icon}
    <span>{template.original_filename}</span>
  </div>;
}

function GeneratedDocumentTracker(props: { templatesSelected: DocumentLibraryItem[] }) {

  const generateDocument = usePost('/ai/document_library/queue_generate');
  const [tasks, setTasks] = useState<{ taskId: string, template: DocumentLibraryItem }[]>([]);
  const [remaining, setRemaining] = useState(0);
  const cancel = useRef(new AbortController());

  async function doGenerate() {
    let tasksToWatch: { taskId: string, template: DocumentLibraryItem }[] = [];
    for (const template of props.templatesSelected) {
      const result = await generateDocument({ config: { templateId: template.id } }, cancel.current.signal);

      if (!result.data || result.error) {
        continue;
      }

      tasksToWatch.push({
        taskId: result.data.taskId,
        template
      });
    }

    setRemaining(tasksToWatch.length);
    setTasks(tasksToWatch);
  }

  const generationTaskStatuses = useMemo(() =>
    tasks.map(({ taskId, template }) =>
      <GenerationStatus
        key={taskId}
        taskId={taskId}
        template={template}
        onComplete={() => setRemaining(prev => Math.max(prev - 1, 0))}
      />)
  , [tasks]);

  return <Card>
    <CardHeader title={<div className="flex items-center space-x-4">
      <div hidden={!tasks.length}>
        <ProgressCircle
          className="w-5 h-5"
          value={((tasks.length - remaining) / tasks.length) * 100}
        />
      </div>
      <span>Generated Documents</span>
    </div>}
    actionButton={
      <div className="space-x-2">
        <Button
          icon={<Icon name="PlayIcon" />}
          isDisabled={remaining > 0 || props.templatesSelected.length === 0}
          onClick={() => doGenerate()}>
          Generate {props.templatesSelected.length ? `(${props.templatesSelected.length})` : ''}
        </Button>
        <Button
          icon={<Icon name="StopIcon" />}
          isDisabled={remaining === 0}
          variant={remaining === 0 ? "secondary" : "danger"}
          onClick={() => {
            cancel.current.abort('generation cancelled');
            cancel.current = new AbortController();
          }}>
        </Button>
      </div>
    } />
    <CardBody className="space-y-2">
      {generationTaskStatuses}
    </CardBody>
  </Card>
}
